import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';

import { TableCell, TableRow, makeStyles } from '@material-ui/core';

import useNavigate from 'hooks/useNavigate';
import Zoom from '@material-ui/core/Zoom';
import useSorting from '../../common/hooks/useSorting';
import { getCellFormat } from '../utils';
import TypographyWithTooltip from '../../../../shared/TypographyWithTooltip';

const useStyles = makeStyles(theme => ({
  row: {
    textDecoration: 'none',
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding,
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  linkRow: {
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.palette.boxShadow.default,
      backgroundColor: `${theme.palette.primary.main}40`
    },
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: '1px',
      textAlign: theme.customStyles.tdRightAlign.textAlign,
      '&:first-child': {
        textAlign: theme.customStyles.tdLeftAlign.textAlign
      },
      '&:nth-child(2)': {
        maxWidth: theme.customStyles.nthChild.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow,
        textAlign: theme.customStyles.tdLeftAlign.textAlign
      },
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  negative: {
    color: theme.palette.error.main
  },
  manager: {
    color: theme.palette.accent.main,
    userSelect: 'none'
  },
  temf: {
    backgroundColor: theme.palette.grey.medium,
    '& td': {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.customStyles.tableTemf.fontSize,
      padding: theme.customStyles.tableRow.padding,
      textAlign: theme.customStyles.tdRightAlign.textAlign,
      '&:nth-child(2)': {
        textAlign: theme.customStyles.tdLeftAlign.textAlign
      }
    }
  }
}));

const Body = ({ rows, totalRow, columns, sortBy, sortOrder, handleContextMenu }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { getColumnSorting } = useSorting();

  const renderSingleRow = row =>
    columns.map(column => {
      const { value, className } = getCellFormat(column.key, row[column.key]);
      const key = `${column.key}-${row.rank}`;

      return (
        <TableCell key={key}>
          <TypographyWithTooltip
            variant="subtitle1"
            tooltipProps={{
              value,
              placement: 'right',
              TransitionComponent: Zoom,
              arrow: true
            }}
          >
            <span className={classes[className]}>{value}</span>
          </TypographyWithTooltip>
        </TableCell>
      );
    });

  const renderBody = () => {
    const sortKey = sortBy || 'rank';
    const sortedRows = rows.sort(getColumnSorting(sortKey, sortOrder));

    return sortedRows.map(row => (
      <TableRow
        key={row.rank}
        onClick={() => navigate(`/canned/${row.timeSeriesId}`)}
        onContextMenu={e => handleContextMenu(e, `/canned/${row.timeSeriesId}`)}
        className={classes.linkRow}
      >
        {renderSingleRow(row)}
      </TableRow>
    ));
  };

  const renderTemf = () => {
    const { strategy, value, allocation, monthToDateValue, yearToDateValue, attribute } = totalRow;

    return (
      <TableRow className={classes.temf}>
        <TableCell />
        <TableCell>{strategy}</TableCell>
        <TableCell>{numeral(value).format('$0,0')}</TableCell>
        <TableCell>{numeral(allocation).format('0.00%')}</TableCell>
        <TableCell>{numeral(yearToDateValue).format('0.00%')}</TableCell>
        <TableCell>{numeral(monthToDateValue).format('0.00%')}</TableCell>
        <TableCell>{numeral(attribute).format('0.00%')}</TableCell>
        <TableCell />
      </TableRow>
    );
  };

  return (
    <>
      {renderBody()}
      {totalRow && renderTemf()}
    </>
  );
};

Body.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  totalRow: PropTypes.shape({
    strategy: PropTypes.string,
    value: PropTypes.number,
    allocation: PropTypes.number,
    monthToDateValue: PropTypes.number,
    yearToDateValue: PropTypes.number
  }).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  handleContextMenu: PropTypes.func.isRequired
};

Body.defaultProps = {
  sortBy: '',
  sortOrder: 'desc'
};

export default Body;
